import { Timestamp, type SetOptions } from 'firebase/firestore';
import type { CollectionEntry } from '../types';

/**
 * Write options for every `collectionSummary` write.
 *
 * Never `{ merge: true }`: merge deep-merges nested maps, so a `bySet` entry for
 * a set the user no longer owns survives the write. Since
 * `collectionSummaryTotalsEqual` compares `bySet` key counts, one stale key
 * makes `summaryNeedsHeal` true forever — a heal write plus a full-collection
 * read every session, for a summary that can never converge. `mergeFields`
 * replaces the field wholesale and leaves the rest of the user doc alone.
 */
export const COLLECTION_SUMMARY_SET_OPTIONS: SetOptions = {
  mergeFields: ['collectionSummary'],
};

export interface CollectionSummary {
  totalCards: number;
  uniqueCards: number;
  totalFoilCards: number;
  /** Unique owned cards per set code (from the card-id prefix). */
  bySet: Record<string, number>;
  revision: number;
  updatedAt: number;
}

export function emptyCollectionSummary(revision = 1): CollectionSummary {
  return {
    totalCards: 0,
    uniqueCards: 0,
    totalFoilCards: 0,
    bySet: {},
    revision,
    updatedAt: Date.now(),
  };
}

function isSetCodeToken(code: string): boolean {
  return /^[A-Z][A-Z0-9]{1,4}$/.test(code);
}

export function setCodeFromCardId(cardId: string): string | null {
  const slash = cardId.indexOf('/');
  if (slash > 0) {
    const code = cardId.slice(0, slash).trim().toUpperCase();
    return isSetCodeToken(code) ? code : null;
  }
  const hyphen = cardId.indexOf('-');
  if (hyphen <= 0) return null;
  const first = cardId.slice(0, hyphen).trim().toUpperCase();
  if (first === 'PREVIEW') {
    const rest = cardId.slice(hyphen + 1);
    const next = rest.indexOf('-');
    const second = (next > 0 ? rest.slice(0, next) : rest).trim().toUpperCase();
    return isSetCodeToken(second) ? second : null;
  }
  return isSetCodeToken(first) ? first : null;
}

export function summarizeCollection(
  entries: Record<string, CollectionEntry>,
  revision = 1,
): CollectionSummary {
  const bySet: Record<string, number> = {};
  let totalCards = 0;
  let uniqueCards = 0;
  let totalFoilCards = 0;
  for (const [cardId, entry] of Object.entries(entries)) {
    if (!entry.quantity) continue;
    uniqueCards += 1;
    totalCards += entry.quantity;
    totalFoilCards += entry.quantityFoil ?? 0;
    const set = setCodeFromCardId(cardId);
    if (set) bySet[set] = (bySet[set] ?? 0) + 1;
  }
  return {
    totalCards,
    uniqueCards,
    totalFoilCards,
    bySet,
    revision,
    updatedAt: Date.now(),
  };
}

function bumpSetCount(
  bySet: Record<string, number>,
  cardId: string,
  delta: number,
): Record<string, number> {
  if (!delta) return bySet;
  const set = setCodeFromCardId(cardId);
  if (!set) return bySet;
  const next = { ...bySet };
  const value = Math.max(0, (next[set] ?? 0) + delta);
  if (value === 0) delete next[set];
  else next[set] = value;
  return next;
}

/** Incremental update for a single card qty change. Bumps revision. */
export function applyCollectionEntryDelta(
  summary: CollectionSummary,
  cardId: string,
  previousQuantity: number,
  previousFoil: number,
  nextQuantity: number,
  nextFoil: number,
): CollectionSummary {
  const wasOwned = previousQuantity > 0;
  const isOwned = nextQuantity > 0;
  const uniqueDelta = (isOwned ? 1 : 0) - (wasOwned ? 1 : 0);
  return {
    totalCards: Math.max(0, summary.totalCards + (nextQuantity - previousQuantity)),
    uniqueCards: Math.max(0, summary.uniqueCards + uniqueDelta),
    totalFoilCards: Math.max(0, summary.totalFoilCards + (nextFoil - previousFoil)),
    bySet: bumpSetCount(summary.bySet, cardId, uniqueDelta),
    revision: summary.revision + 1,
    updatedAt: Date.now(),
  };
}

export function collectionSummaryTotalsEqual(
  a: CollectionSummary | null | undefined,
  b: CollectionSummary | null | undefined,
): boolean {
  if (!a || !b) return false;
  if (
    a.totalCards !== b.totalCards ||
    a.uniqueCards !== b.uniqueCards ||
    a.totalFoilCards !== b.totalFoilCards
  ) {
    return false;
  }
  const aKeys = Object.keys(a.bySet);
  const bKeys = Object.keys(b.bySet);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a.bySet[key] === b.bySet[key]);
}

export function collectionSummariesEqual(
  a: CollectionSummary | null | undefined,
  b: CollectionSummary | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return a == null && b == null;
  return a.revision === b.revision && collectionSummaryTotalsEqual(a, b);
}

/**
 * True when the stored summary disagrees with one derived from live entries.
 *
 * Compares content only — `revision` and `updatedAt` are expected to differ.
 * Recomputing and comparing is deliberate: `summarizeCollection` skips cards
 * whose id yields no set code, so `bySet` can legitimately sum to less than
 * `uniqueCards` and any sum-based check would report drift forever.
 */
export function summaryNeedsHeal(
  existing: CollectionSummary | null | undefined,
  canonical: CollectionSummary,
): boolean {
  if (!existing) return true;
  return !collectionSummaryTotalsEqual(existing, canonical);
}

function readTimestampMillis(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value && typeof value === 'object' && 'toMillis' in value) {
    const toMillis = (value as { toMillis?: () => number }).toMillis;
    if (typeof toMillis === 'function') return toMillis.call(value);
  }
  return Date.now();
}

export function parseCollectionSummary(raw: unknown): CollectionSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  const totalCards = Number(data.totalCards);
  const uniqueCards = Number(data.uniqueCards);
  const totalFoilCards = Number(data.totalFoilCards);
  const revision = Number(data.revision);
  if (
    !Number.isFinite(totalCards) ||
    !Number.isFinite(uniqueCards) ||
    !Number.isFinite(totalFoilCards) ||
    !Number.isFinite(revision)
  ) {
    return null;
  }
  const bySet: Record<string, number> = {};
  if (data.bySet && typeof data.bySet === 'object') {
    for (const [key, value] of Object.entries(data.bySet as Record<string, unknown>)) {
      const n = Number(value);
      if (key && Number.isFinite(n) && n > 0) bySet[key.toUpperCase()] = n;
    }
  }
  return {
    totalCards: Math.max(0, Math.floor(totalCards)),
    uniqueCards: Math.max(0, Math.floor(uniqueCards)),
    totalFoilCards: Math.max(0, Math.floor(totalFoilCards)),
    bySet,
    revision: Math.max(0, Math.floor(revision)),
    updatedAt: readTimestampMillis(data.updatedAt),
  };
}

export function collectionSummaryToFirestore(summary: CollectionSummary): Record<string, unknown> {
  return {
    totalCards: summary.totalCards,
    uniqueCards: summary.uniqueCards,
    totalFoilCards: summary.totalFoilCards,
    bySet: summary.bySet,
    revision: summary.revision,
    updatedAt: Timestamp.fromMillis(summary.updatedAt),
  };
}

/**
 * Shape marker the projection must carry to be considered current. Duplicated
 * from `features/teams/types` to keep this module free of feature imports; the
 * two are asserted equal in `collectionSummary.test.ts`.
 */
const CURRENT_TEAM_PROJECTION_VERSION = 1;

/**
 * True when the team projection was written from this summary revision *and*
 * in the current shape.
 *
 * Requiring `v` is what migrates the legacy tuple-array documents: they read
 * as stale, so `useTeamCollectionHeal` rewrites them as maps on the owner's
 * next session. No backfill script.
 */
export function isTeamProjectionCurrent(
  projection: { revision?: unknown; v?: unknown } | null | undefined,
  summary: CollectionSummary | null | undefined,
): boolean {
  if (!summary || typeof summary.revision !== 'number') return false;
  if (!projection || typeof projection.revision !== 'number') return false;
  if (projection.v !== CURRENT_TEAM_PROJECTION_VERSION) return false;
  return projection.revision === summary.revision;
}
